<template>
  <sf-dialog v-model="dialog" :width="800" scrollable attach @keydown.esc="cancel">
    <template #activator="{ props }">
      <component
        :is="bigButton ? SfLargeButton : SfTextButton"
        ref="punchout-button"
        class="pw-punchout-button"
        :block="$vuetify.display.xs"
        :loading="loading"
        :disabled="disabled"
        v-bind="props"
        @click="showPunchoutPopup"
      >
        {{ $t('punchout.backToProcurement') }}
      </component>
    </template>
    <v-card>
      <v-card-item>
        <v-card-title>
          <sf-heading>{{ $t('punchout.backToProcurement') }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text>
        <loading-spinner v-if="loading" class="fill-height" flat />
        <v-data-table
          v-else
          :headers="headers"
          :items="punchoutFormData.items"
          :row-props="getRowProps"
          :items-per-page="-1"
        >
          <template #bottom />
          <template #[`item.pos`]="{ item }">
            {{ punchoutFormData.items.indexOf(item) + 1 }}
          </template>
          <template #[`item.priceForOne`]="{ item }">
            {{ formatMoney(item.priceForOne) }}
          </template>
          <template #[`item.sum`]="{ item }">
            {{
              formatMoney({
                value: item.quantity * item.priceForOne.value,
                currency: item.priceForOne.currency
              })
            }}
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <punchout-form :form="punchoutFormData" :is-quote="quoteId !== undefined" />
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfLargeButton from '@/components/button/SfLargeButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import { formatMoney } from '@/modules/product/helpers'
import PunchoutForm from '@/modules/punchout/components/PunchoutForm.vue'
import usePunchout from '@/modules/punchout/usePunchout'
import useQuote from '@/modules/quote/useQuote'
import { PunchoutEvent } from '@/modules/tracking/events/punchout'
import { trackEvent } from '@/modules/tracking/useTracking'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DataTableHeader } from 'vuetify'

const componentProps = defineProps({
  supplierCartId: {
    type: String,
    default: undefined
  },
  quoteId: {
    type: String,
    default: undefined
  },
  bigButton: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const { t } = useI18n()

const dialog = ref(false)
const headers: DataTableHeader[] = [
  {
    title: t('punchout.table.position').toString(),
    value: 'pos',
    align: 'start'
  },
  {
    title: t('punchout.table.title').toString(),
    value: 'displayName'
  },
  {
    title: t('punchout.table.quantity').toString(),
    value: 'quantity',
    align: 'end'
  },
  {
    title: t('punchout.table.price').toString(),
    value: 'priceForOne',
    align: 'end'
  },
  {
    title: t('punchout.table.sum').toString(),
    value: 'sum',
    align: 'end'
  }
]
const { punchoutFormData, loading } = storeToRefs(usePunchout())

const showPunchoutPopup = () => {
  if (componentProps.quoteId) {
    // close event stream to prevent closing dialog when quote is
    // updating because of loading punchout form (quote is set to Won)
    useQuote().closeQuoteUpdateStream()
    usePunchout().loadPunchoutQuoteForm(componentProps.quoteId)
  } else {
    usePunchout().loadPunchoutCartForm(componentProps.supplierCartId)
  }
  dialog.value = true
  trackEvent(PunchoutEvent.OpenBackToSystem)
}

const cancel = () => {
  usePunchout().$reset()
  dialog.value = false
  if (componentProps.quoteId) {
    useQuote().openQuoteUpdateStream(componentProps.quoteId, false)
  }
}

const getRowProps = () => {
  return { class: 'product-row' }
}
</script>
